/**
 * TwoFactorPage — Activation / désactivation du 2FA (TOTP).
 *
 * Refonte R5 (mai 2026) :
 *   - Header glass cohérent DashboardPage (LogoMark Landmark + bouton Retour + ThemeToggle)
 *   - Card glass principale avec LogoMark ShieldCheck + Badge state (Activée/Désactivée)
 *   - 3 étapes setup : QR code en Card glass blanche + codes backup en grid + input 6 chiffres
 *
 * Logique métier 100% préservée :
 *   - api.twoFactor.setup() → setupData (qrCodeDataUrl, secret, backupCodes)
 *   - api.twoFactor.verify(code) + refreshMe()
 *   - api.twoFactor.disable() + refreshMe()
 *   - Input code avec inputMode numeric, pattern, maxLength 6, replace /\D/g
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Field,
  Card,
  Alert,
  Badge,
  LogoMark,
  SectionEyebrow,
} from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import {
  ArrowLeft,
  ShieldCheck,
  Landmark,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';

export function TwoFactorPage() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [setupData, setSetupData] = useState<{
    qrCodeDataUrl: string;
    secret: string;
    backupCodes: string[];
  } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  async function startSetup() {
    setError(null);
    setLoading(true);
    try {
      const res = await api.twoFactor.setup();
      setSetupData(res);
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error);
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode() {
    setError(null);
    setLoading(true);
    try {
      await api.twoFactor.verify(code);
      await refreshMe();
      setSetupData(null);
      setCode('');
      setSuccess('2FA activé avec succès. Vous serez invité à saisir un code à la prochaine connexion.');
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error);
    } finally {
      setLoading(false);
    }
  }

  async function disable() {
    setError(null);
    setLoading(true);
    try {
      await api.twoFactor.disable();
      await refreshMe();
      setSuccess('2FA désactivé.');
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen text-ink">
      {/* ===== Topbar glass (cohérent DashboardPage R4) ===== */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
        style={{
          backgroundImage:
            'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
            'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
        }}
      >
        <LogoMark icon={Landmark} accent="tech" size="md" aria-label="VPC" />
        <div className="min-w-0">
          <SectionEyebrow>Espace personnel</SectionEyebrow>
          <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
            Sécurité 2FA
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Tableau de bord</span>
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6 animate-fade-in">
        <Card variant="glass" padded>
          {/* En-tête de la card : icon + titre + badge état */}
          <div className="flex items-start justify-between gap-3 pb-4 border-b border-border">
            <div className="flex items-center gap-3 min-w-0">
              <LogoMark icon={ShieldCheck} accent="tech" size="md" />
              <div className="min-w-0">
                <h1 className="font-display text-lg font-semibold tracking-r-snug text-ink">
                  Authentification à deux facteurs
                </h1>
                <p className="mt-0.5 text-sm text-ink-muted">
                  Sécurise votre compte avec un second facteur (TOTP)
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              {user?.twoFactorEnabled ? (
                <Badge variant="success">
                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                  Activée
                </Badge>
              ) : (
                <Badge variant="warning">Désactivée</Badge>
              )}
            </div>
          </div>

          {/* Corps de la card */}
          <div className="pt-4 space-y-4">
            {error && (
              <Alert variant="error" icon={AlertCircle}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" icon={CheckCircle2}>
                {success}
              </Alert>
            )}

            {/* ========== ÉTAT 1 : 2FA non activé, pas de setupData ========== */}
            {!user?.twoFactorEnabled && !setupData && (
              <>
                <p className="text-sm leading-relaxed text-ink">
                  Le 2FA ajoute une seconde couche de sécurité. À chaque connexion, en plus de
                  votre mot de passe, vous saisirez un code à 6 chiffres généré par une application
                  comme <strong>Google Authenticator</strong>, <strong>Authy</strong> ou{' '}
                  <strong>Microsoft Authenticator</strong>.
                </p>
                <Alert variant="info" icon={Info}>
                  Le 2FA est <strong>obligatoire</strong> pour les rôles administrateurs et
                  managers, <strong>recommandé</strong> pour tous les agents.
                </Alert>
                <Button onClick={startSetup} loading={loading}>
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  Activer le 2FA
                </Button>
              </>
            )}

            {/* ========== ÉTAT 2 : setupData chargé, 3 étapes ========== */}
            {setupData && (
              <>
                {/* Étape 1 : QR code */}
                <div>
                  <SectionEyebrow as="div">Étape 1</SectionEyebrow>
                  <p className="mt-1 text-sm font-medium text-ink">
                    Scannez ce QR code avec votre application d'authentification
                  </p>
                  <div className="mt-3 flex justify-center rounded-r-xl border border-border bg-white p-4 shadow-soft">
                    <img
                      src={setupData.qrCodeDataUrl}
                      alt="QR code 2FA — scannez avec votre application d'authentification"
                      className="rounded-md"
                    />
                  </div>
                  <details className="mt-2 text-xs text-ink-muted">
                    <summary className="cursor-pointer select-none hover:text-ink">
                      Code manuel (si vous ne pouvez pas scanner)
                    </summary>
                    <code className="mt-2 block break-all rounded-r-md border border-border bg-surface/40 p-2 font-mono text-2xs text-ink">
                      {setupData.secret}
                    </code>
                  </details>
                </div>

                {/* Étape 2 : Codes de récupération */}
                <Alert variant="warning" icon={AlertCircle}>
                  <p className="font-medium">Étape 2 — Conservez ces 10 codes de récupération</p>
                  <p className="mt-1 text-xs">
                    En cas de perte de votre appareil, ils permettent de récupérer l'accès au
                    compte. À conserver dans un gestionnaire de mots de passe.
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-1.5 font-mono text-xs">
                    {setupData.backupCodes.map((c) => (
                      <span
                        key={c}
                        className="rounded-r-md border border-border bg-bg/80 px-2 py-1 text-center text-ink"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </Alert>

                {/* Étape 3 : Vérification */}
                <Field label="Étape 3 — Saisissez un code généré par l'app" htmlFor="code">
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    className="h-12 text-center font-mono text-lg tracking-widest"
                  />
                </Field>

                <Button onClick={verifyCode} loading={loading} disabled={code.length !== 6}>
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  Vérifier et activer
                </Button>
              </>
            )}

            {/* ========== ÉTAT 3 : 2FA activé ========== */}
            {user?.twoFactorEnabled && (
              <>
                <Alert variant="success" icon={CheckCircle2}>
                  Le 2FA est activé sur votre compte. Bravo, votre accès est plus sécurisé.
                </Alert>
                <Button variant="danger" onClick={disable} loading={loading}>
                  Désactiver le 2FA
                </Button>
              </>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}
